import { useState } from 'react';
import { getUser } from '../../../../services/auth';
import { Aviso, Carregando, useDecisao } from '../../../common/interface';
import { moeda } from '../../formato';
import type { CooperadoAba } from '../../types';
import { vincularGrupoNaAtf } from '../../grupos/useGruposPassageiros';
import { useSaldoResumo } from '../../useSaldoResumo';
import { CoberturaPixSheet } from './CoberturaPixSheet';
import { erroDoPasso, primeiroPassoIncompleto } from './validacaoDoPasso';
import { useDadosIniciais } from './useDadosWizard';
import { useEmissao } from './useEmissao';
import { bloqueioEmissaoDe, causaDoMotivo } from './bloqueio';
import { WizardStepper } from './WizardStepper';
import { WizardRail } from './WizardRail';
import { WizardRodape } from './WizardRodape';
import { PassoCondutor } from './PassoCondutor';
import { PassoVeiculo } from './PassoVeiculo';
import { PassoRota } from './PassoRota';
import { PassoTrajeto } from './PassoTrajeto';
import { PassoDatas } from './PassoDatas';
import { PassoValor } from './PassoValor';
import { PassoPassageiros } from './PassoPassageiros';
import { PassoRevisao } from './PassoRevisao';
import { RetomadaDaEmissao } from './RetomadaDaEmissao';
import { ESTADO_INICIAL } from './types';

// Ordem exata do handoff §2: condutor(1) → veiculo(2) → rota(3) → datas(4)
// → trajeto(5) → passageiros(6) → valor(7) → revisao(8).
const TITULOS_PASSO = [
  'Quem vai dirigir?', 'Qual veículo?', 'Para onde?', 'Quando?',
  'Qual o trajeto?', 'Quem vai de passageiro?', 'Qual o valor?', 'Confira antes de solicitar',
];
const TOTAL_PASSOS = TITULOS_PASSO.length;

/**
 * O wizard de emissão de ATF — porta o handoff §2 (área full-bleed, coluna
 * de 620px + rail de 288px, stepper, barra fixa de rodapé) sobre a lógica
 * já construída na Fase 4 (7 passos + sequência de 3 estágios com
 * idempotência e retomada — `useEmissao.ts`).
 */
interface WizardEmissaoProps {
  /** Sem isto, a tela de bloqueio não tem para onde mandar o cooperado. */
  onNavegar?: (aba: CooperadoAba) => void;
}

export function WizardEmissao({ onNavegar }: WizardEmissaoProps) {
  const { condutores, avisoEscopo, veiculos, parametros, elegibilidade, tarifa, carregando, erro: erroCarga,
    recarregar, revalidarElegibilidade } = useDadosIniciais();
  const [avisoPosPagamento, setAvisoPosPagamento] = useState<string | null>(null);
  const [idGrupoAplicado, setIdGrupoAplicado] = useState<number | null>(null);
  /** Preenchido quando o servidor recusa por saldo e a cobertura é possível. */
  const [coberturaMotivo, setCoberturaMotivo] = useState<string | null>(null);
  const { saldo, tipoEmissao } = useSaldoResumo();
  const { janela, confirmar } = useDecisao();
  const usuario = getUser();
  const [passo, setPasso] = useState(0);
  const [estado, setEstado] = useState(ESTADO_INICIAL);
  const [avisoCredito, setAvisoCredito] = useState<{ mensagem: string; acao: string } | null>(null);

  const {
    estagio, mensagem, erro, documentoEmitido, emitir, pendente, desfechoDaRetomada, retomar, descartarPendente,
  } = useEmissao({
    idCooperado: usuario?.idUsuario ?? null,
    // `ofertarCobertura` só chega aqui quando o backend diz que o cooperado
    // pode pagar a diferença — a regra é dele, o cliente não a recompõe.
    podeCobrirPorPix: saldo?.permiteCobrirPorPix === true,
    onCreditoRecusado: (msg, acao) => {
      if (acao === 'ofertarCobertura') { setCoberturaMotivo(msg); return; }
      setAvisoCredito({ mensagem: msg, acao });
    },
  });

  // A emissão parada vem antes de tudo, inclusive do bloqueio: o documento fiscal
  // já existe, e quem decide se a ATF sai é o servidor.
  if (pendente) {
    return (
      <RetomadaDaEmissao
        registro={pendente}
        estagio={estagio}
        mensagem={mensagem}
        erro={erro}
        desfecho={desfechoDaRetomada}
        onConcluir={() => { void retomar(); }}
        onOutraViagem={() => { descartarPendente(); setEstado(ESTADO_INICIAL); setPasso(0); }}
        onNavegar={onNavegar}
      />
    );
  }

  if (carregando) {
    return <div className="co-assistente"><div className="co-assistente-coluna"><Carregando rotulo="Carregando a emissão" /></div></div>;
  }
  if (erroCarga) {
    return (
      <section className="co-bloqueio" aria-labelledby="co-erro-carga">
        <h2 id="co-erro-carga" className="co-titulo-tela">A emissão não abriu</h2>
        <p className="co-apoio">{erroCarga}</p>
        <div className="co-acoes">
          <button type="button" className="co-btn co-btn-primario co-btn-largo" onClick={recarregar}>Tentar de novo</button>
        </div>
      </section>
    );
  }

  /**
   * O veredito DESTE cooperado, procurado pelo id.
   *
   * Era `Object.values(...)[0]`, e o mapa vem com o logado **mais os
   * subordinados** — o backend expande a hierarquia sempre. Com chaves
   * numéricas, `Object.values` devolve a de MENOR id primeiro, não a do
   * logado: um cooperado adimplente cujo irmão de id menor devia ficava
   * barrado do wizard inteiro, com a dívida de outra pessoa e sem saída na
   * tela; e o inverso passava o gate para cair no 402 depois do CT-e.
   */
  const meuVeredito = usuario?.idUsuario != null
    ? elegibilidade?.motoristas?.[String(usuario.idUsuario)]
    : undefined;
  const bloqueio = bloqueioEmissaoDe({
    bloqueado: meuVeredito?.bloqueado ?? false,
    causaBloqueio: meuVeredito?.motivo ? causaDoMotivo(meuVeredito.motivo) : null,
    bloqueioMsg: meuVeredito?.motivo ?? null,
    exibeCredito: false, // pós-pago por padrão — o veredito de crédito real vem do 402 no submit
    semCreditoParaEmitir: false,
    bloqueioCreditoMsg: null,
  });

  if (bloqueio) {
    return (
      <section className="co-bloqueio" aria-labelledby="co-bloqueio-titulo">
        <h2 id="co-bloqueio-titulo" className="co-titulo-tela">{bloqueio.titulo}</h2>
        <p className="co-apoio">{bloqueio.mensagem}</p>

        {avisoPosPagamento && <p className="co-nota co-nota-sucesso" role="status">{avisoPosPagamento}</p>}

        {/* A saída que faltava. `rotuloDoBloqueio` já dizia "Ver Minhas Faturas"
            desde a 0034, mas nada renderizava a ação: o cooperado lia que estava
            bloqueado e não tinha para onde ir. */}
        {bloqueio.causa === 'inadimplencia' && onNavegar && (
          <div className="co-acoes">
            <button
              type="button"
              className="co-btn co-btn-primario co-btn-largo"
              onClick={async () => {
                onNavegar('faturas');
                // Ao voltar, o veredito precisa ser reconsultado — senão quem
                // pagou vê a mesma tela e conclui que o pagamento falhou.
                const consultou = await revalidarElegibilidade();
                setAvisoPosPagamento(consultou
                  ? 'Se você acabou de pagar, a baixa pode levar alguns minutos. Toque de novo para verificar.'
                  : 'Não conseguimos verificar agora. Tente novamente em instantes.');
              }}
            >
              {bloqueio.rotuloAcao}
            </button>
          </div>
        )}
      </section>
    );
  }

  function atualizar<K extends keyof typeof ESTADO_INICIAL>(campo: K, valor: (typeof ESTADO_INICIAL)[K]) {
    setEstado(e => ({ ...e, [campo]: valor }));
  }

  const ultimoPasso = passo === TOTAL_PASSOS - 1;

  /**
   * O que impede sair do passo atual.
   *
   * `WizardRodape` implementa `desabilitado`/`motivoBloqueio` desde a 0034 e
   * ninguém os passava: o erro virava texto cinza dentro do passo e o
   * "Continuar" seguia clicável, então dava para chegar à revisão com o
   * formulário vazio — e a recusa só aparecia depois, às vezes com o documento
   * fiscal já criado.
   */
  const ctxValidacao = {
    estado,
    permitirReduzirKm: parametros?.permitirReduzirKm ?? false,
    temVeiculos: veiculos.length > 0,
    capacidade: veiculos.find(v => v.idVeiculo === estado.idVeiculo)?.capacidadePassageiros,
  };
  const erroPassoAtual = erroDoPasso(passo, ctxValidacao);

  const conteudo = [
    <PassoCondutor
      condutores={condutores}
      avisoEscopo={avisoEscopo}
      idCondutorSgtf={estado.idCondutorSgtf}
      onCondutor={c => setEstado(e => ({
        ...e, idCondutorSgtf: c.idCondutorSgtf, condutorNomeCpf: c.condutorNomeCpf, condutorNome: c.nome,
        idUsuarioCondutor: c.idUsuarioCondutor ?? null,
      }))}
    />,
    <PassoVeiculo
      veiculos={veiculos}
      idVeiculo={estado.idVeiculo}
      onVeiculo={v => setEstado(e => ({
        ...e, idVeiculo: v.idVeiculo, placa: v.placa,
        // Nunca nulo: a lista já é a interseção, então todo veículo ofertado
        // tem o id que o portal do DER conhece.
        idVeiculoSgtf: v.idVeiculoSgtf,
      }))}
    />,
    <PassoRota
      municipioOrigemIndice={estado.municipioOrigemIndice} municipioDestinoIndice={estado.municipioDestinoIndice}
      municipioOrigemNome={estado.municipioOrigemNome} municipioDestinoNome={estado.municipioDestinoNome}
      onOrigem={m => setEstado(e => ({ ...e, municipioOrigemIndice: m.indice, municipioOrigemNome: m.nome, cMunIni: m.codIbge ?? '' }))}
      onDestino={m => setEstado(e => ({ ...e, municipioDestinoIndice: m.indice, municipioDestinoNome: m.nome, cMunFim: m.codIbge ?? '' }))}
    />,
    // Datas antes do trajeto, como os títulos, o stepper e a validação: invertido, o
    // passo 4 pedia a data sem mostrar o campo, e ninguém emitia (teste 0034-ordem-dos-passos).
    <PassoDatas
      dataIda={estado.dataIda} horarioIda={estado.horarioIda} dataVolta={estado.dataVolta} horarioVolta={estado.horarioVolta}
      somenteIda={estado.somenteIda}
      onChange={(campo, valor) => atualizar(campo, valor)}
    />,
    <PassoTrajeto
      cMunIni={estado.cMunIni} cMunFim={estado.cMunFim}
      somenteIda={estado.somenteIda} permitirSomenteIda={parametros?.permitirSomenteIda ?? false}
      permitirReduzirKm={parametros?.permitirReduzirKm ?? false}
      kmFiscalIncluiVolta={parametros?.kmFiscalIncluiVolta ?? true}
      kmIda={estado.kmIda} kmVolta={estado.kmVolta} itinerario={estado.itinerario}
      onSomenteIda={v => atualizar('somenteIda', v)}
      onKm={(ida, volta, idaBase, voltaBase) => setEstado(e => ({ ...e, kmIda: ida, kmVolta: volta, kmIdaBase: idaBase, kmVoltaBase: voltaBase }))}
      onItinerario={v => atualizar('itinerario', v)}
    />,
    <PassoPassageiros
      tomaNome={estado.tomaNome} tomaCpfCnpj={estado.tomaCpfCnpj} passageiros={estado.passageiros}
      capacidade={veiculos.find(v => v.idVeiculo === estado.idVeiculo)?.capacidadePassageiros}
      onTomador={(nome, cpfCnpj) => setEstado(e => ({ ...e, tomaNome: nome, tomaCpfCnpj: cpfCnpj }))}
      onPassageiros={p => atualizar('passageiros', p)}
      onGrupoAplicado={setIdGrupoAplicado}
    />,
    <PassoValor
      valorCentavos={estado.valorCentavos} kmIda={estado.kmIda} kmVolta={estado.kmVolta} somenteIda={estado.somenteIda}
      tarifa={tarifa} parametros={parametros}
      onChange={v => atualizar('valorCentavos', v)}
    />,
    <PassoRevisao
      estado={estado} estagio={estagio} mensagem={mensagem} erro={erro} prePago={tipoEmissao === 'PREPAGO'}
      onSolicitar={async () => {
        // A primeira tentativa emite o documento fiscal e o manda à SEFAZ, que não
        // se desfaz por aqui. As seguintes só continuam o mesmo documento.
        if (!documentoEmitido) {
          const sim = await confirmar({
            titulo: 'Solicitar a ATF?',
            contexto: `${estado.municipioOrigemNome} → ${estado.municipioDestinoNome} · ${moeda(estado.valorCentavos / 100)}`,
            consequencia: `Emite o documento fiscal (CT-e), que vai à SEFAZ e não se desfaz por aqui, e depois pede a ATF ao DER. ${tipoEmissao === 'PREPAGO' ? 'O valor sai do seu crédito.' : 'O valor entra na sua fatura.'}`,
            rotuloConfirmar: 'Solicitar ATF',
          });
          if (!sim) return;
        }
        // O retorno de `emitir` era descartado, e com ele o atfLogId — sem ele
        // não havia como anotar qual grupo foi usado.
        const r = await emitir(estado);
        // `null` é falha: anotar aí registraria vínculo de uma ATF que não existe.
        if (r && idGrupoAplicado) await vincularGrupoNaAtf(r.atfLogId, idGrupoAplicado);
      }}
    />,
  ][passo];

  const processando = estagio !== 'ocioso' && estagio !== 'concluido';
  // Na revisão, voltar só enquanto o documento não existe: depois dele a viagem
  // não muda, e a ATF descreveria outra coisa que o CT-e.
  const podeVoltar = passo > 0 && !processando && !(ultimoPasso && (documentoEmitido || estagio === 'concluido'));

  return (
    <div className="co-assistente">
      {janela}
      <div className="co-assistente-coluna">
        <WizardStepper
          passoAtual={passo}
          travado={documentoEmitido || processando || estagio === 'concluido'}
          // Voltar é sempre livre; avançar pelo stepper para. Pular um
          // passo em branco levava à revisão com dado faltando, e a
          // recusa só aparecia depois — às vezes com o CT-e já criado.
          onNavegar={destino => {
            if (destino <= passo) { setPasso(destino); return; }
            const pendenteNoCaminho = primeiroPassoIncompleto(ctxValidacao, destino);
            setPasso(pendenteNoCaminho ?? destino);
          }}
        />

        <h2 className="co-titulo-passo">{TITULOS_PASSO[passo]}</h2>

        {avisoCredito && <Aviso onFechar={() => setAvisoCredito(null)}>{avisoCredito.mensagem}</Aviso>}

        {conteudo}
      </div>

      <CoberturaPixSheet
        aberto={coberturaMotivo !== null}
        motivo={coberturaMotivo}
        trajeto={coberturaMotivo === null ? null : {
          // Nome + UF: a rota aceita os dois caminhos, e o painel só opera
          // dentro de MG (é o DER/MG que autoriza).
          origemNome: estado.municipioOrigemNome,
          origemUf: 'MG',
          destinoNome: estado.municipioDestinoNome,
          destinoUf: 'MG',
          somenteIda: estado.somenteIda,
          kmDeclarado: estado.somenteIda ? estado.kmIda : estado.kmIda + estado.kmVolta,
          vTPrest: estado.valorCentavos / 100,
        }}
        onFechar={() => setCoberturaMotivo(null)}
        onLiberada={() => {
          // Fecha e deixa o cooperado tocar em "Solicitar ATF" de novo: o
          // hook é retomável e pula os estágios já concluídos, então o CT-e
          // que já existe não é recriado.
          setCoberturaMotivo(null);
          setAvisoCredito({ mensagem: 'Crédito liberado. Toque em "Solicitar ATF" para continuar.', acao: 'avisar' });
        }}
      />

      <WizardRail passoAtual={passo} estado={estado} tituloRevisao={ultimoPasso} />

      <WizardRodape
        contexto={ultimoPasso ? 'REVISÃO' : `PASSO ${passo + 1} DE ${TOTAL_PASSOS - 1}`}
        motivoBloqueio={ultimoPasso ? null : erroPassoAtual}
        desabilitado={erroPassoAtual != null}
        onVoltar={podeVoltar ? () => setPasso(p => p - 1) : undefined}
        onAvancar={ultimoPasso ? undefined : () => setPasso(p => Math.min(TOTAL_PASSOS - 1, p + 1))}
        rotuloAvancar={passo === TOTAL_PASSOS - 2 ? 'Continuar para revisão' : 'Continuar'}
      />
    </div>
  );
}
